import { Card, CardBody, Col, Row, Table } from "reactstrap";
import { Link } from "react-router-dom";
import { DateOnly, DateTime } from "components/common/Date";
import StatusBadge from "components/common/StatusBadge";
import {
  amcVisitKindLabelMap,
  amcVisitStatusMap,
  AmcVisitStatus,
} from "features/AmcVisitLogs/constants/amc-visit.constants";
import type { AmcVisit } from "features/AmcVisitLogs/types/amc-visit.types";
import { useAmcVisitOverview } from "./AmcVisitOverviewContext";
import { resolveVisitKind } from "features/AmcVisitLogs/utils/amcVisitBilling";
import AssignmentsOverviewCard from "features/WorkAssignments/components/AssignmentsOverviewCard";
import { WorkAssignmentSourceType } from "features/WorkAssignments/constants/work-assignment.constants";
import { useAccess } from "features/Authentication/hooks/useAccess";
import { Permissions } from "features/RolesAndPermissions/constants/permission.constants";
import {
  shouldShowAmcVisitAssignmentActions,
  canReassignAmcVisit,
} from "features/AmcVisitLogs/utils/amcVisitActionPolicy";
import { Timeline, type TimelineStep, type TimelineStepState } from "components/common/Timeline";
import { historyLastMessage } from "utils/status-history";

/**
 * Only IN_PROGRESS is genuine ongoing work — PLANNED is scheduled but not started yet, a
 * resting state, and should render as a static "done" dot rather than pulsing.
 */
function resolveVisitTimelineState(
  status: AmcVisitStatus,
  isLast: boolean,
): TimelineStepState {
  if (status === AmcVisitStatus.CANCELLED || status === AmcVisitStatus.MISSED) {
    return "skipped";
  }
  if (isLast && status === AmcVisitStatus.IN_PROGRESS) return "active";
  return "done";
}

function buildTimeline(visit: AmcVisit): TimelineStep[] {
  const history = visit.statusHistory ?? [];
  return history.map((entry, i) => {
    const isLast = i === history.length - 1;
    const s = entry.status as AmcVisitStatus;
    const label = amcVisitStatusMap[s]?.label ?? entry.status;
    const state = resolveVisitTimelineState(s, isLast);
    const byLabel = entry.user
      ? [entry.user.firstName, entry.user.lastName].filter(Boolean).join(" ").trim() || `#${entry.by}`
      : entry.by != null ? `#${entry.by}` : undefined;
    return {
      label,
      timestamp: entry.at,
      by: byLabel,
      actor: entry.message ?? undefined,
      state,
      color: amcVisitStatusMap[s]?.color,
    };
  });
}

function resolveVisitTimestamp(
  row: AmcVisit,
  key: "created" | "updated",
): string | undefined {
  if (key === "created") {
    const v = row.created_at ?? row.createdAt;
    return v != null && String(v).trim() !== "" ? String(v) : undefined;
  }
  const v = row.updated_at ?? row.updatedAt;
  return v != null && String(v).trim() !== "" ? String(v) : undefined;
}

const OverviewTab = () => {
  const { visit, refetch } = useAmcVisitOverview();
  const { can } = useAccess();
  const canCreateAssignment = can(Permissions.WORK_ASSIGNMENT.CREATE);
  const canUpdateAssignment = can(Permissions.WORK_ASSIGNMENT.UPDATE);

  if (!visit) {
    return null;
  }

  const createdAt = resolveVisitTimestamp(visit, "created");
  const updatedAt = resolveVisitTimestamp(visit, "updated");
  const completionRemarks =
    visit.completionRemarks?.trim() ||
    historyLastMessage(visit.statusHistory, AmcVisitStatus.COMPLETED);

  const cancellationReason = historyLastMessage(
    visit.statusHistory,
    AmcVisitStatus.CANCELLED,
  );

  const locationLine = [visit.address, visit.city, visit.state]
    .filter((s) => s != null && String(s).trim() !== "")
    .join(", ");

  const visitKind = resolveVisitKind(visit);

  return (
    <Row>
      <Col lg={8}>
        <Card>
          <CardBody>
            <div className="text-muted">
              <h6 className="mb-3 text-uppercase">Visit details</h6>
              <div className="table-responsive">
                <Table className="table-borderless mb-0">
                  <tbody>
                    {visit.code ? (
                      <tr>
                        <th
                          className="ps-0"
                          scope="row"
                          style={{ width: "180px" }}
                        >
                          Code
                        </th>
                        <td className="text-muted">{visit.code}</td>
                      </tr>
                    ) : null}
                    {visit.visitNumber != null ? (
                      <tr>
                        <th className="ps-0" scope="row">
                          Visit number
                        </th>
                        <td className="text-muted">{visit.visitNumber}</td>
                      </tr>
                    ) : null}

                    <tr>
                      <th className="ps-0" scope="row">
                        Visit kind
                      </th>
                      <td className="text-muted">
                        {amcVisitKindLabelMap[visitKind]}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Scheduled
                      </th>
                      <td className="text-muted">
                        {visit.scheduledAt ? (
                          <DateOnly date={visit.scheduledAt} />
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Location
                      </th>
                      <td className="text-muted">{locationLine || "—"}</td>
                    </tr>
                    <tr>
                      <th className="ps-0" scope="row">
                        Contract
                      </th>
                      <td className="text-muted">
                        {visit.amcId != null ? (
                          <Link
                            to={`/amc-management/${visit.amcId}`}
                            className="link"
                          >
                            {visit.contract?.code ?? `Contract #${visit.amcId}`}
                          </Link>
                        ) : (
                          "—"
                        )}
                      </td>
                    </tr>
                  </tbody>
                </Table>
              </div>

              {visit.remarks?.trim() ? (
                <div className="pt-3 border-top border-top-dashed mt-4">
                  <h6 className="mb-3 text-uppercase">Visit Remarks</h6>
                  <p className="text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {visit.remarks}
                  </p>
                </div>
              ) : null}

              {completionRemarks ? (
                <div className="pt-3 border-top border-top-dashed mt-4">
                  <h6 className="mb-3 text-uppercase">Completion Remarks</h6>
                  <p className="text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {completionRemarks}
                  </p>
                </div>
              ) : null}

              {cancellationReason ? (
                <div className="pt-3 border-top border-top-dashed mt-4">
                  <h6 className="mb-3 text-uppercase text-danger">Cancellation Reason</h6>
                  <p className="text-muted mb-0" style={{ whiteSpace: "pre-wrap" }}>
                    {cancellationReason}
                  </p>
                </div>
              ) : null}

              <div className="pt-3 border-top border-top-dashed mt-4">
                <Row className="gy-3">
                  <Col lg={3} sm={6}>
                    <div>
                      <p className="mb-2 text-uppercase fw-medium">Created</p>
                      <h5 className="fs-14 mb-0">
                        {createdAt ? (
                          <DateTime date={createdAt} />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </h5>
                    </div>
                  </Col>

                  <Col lg={3} sm={6}>
                    <div>
                      <p className="mb-2 text-uppercase fw-medium">
                        Last updated
                      </p>
                      <h5 className="fs-14 mb-0">
                        {updatedAt ? (
                          <DateTime date={updatedAt} />
                        ) : (
                          <span className="text-muted">—</span>
                        )}
                      </h5>
                    </div>
                  </Col>

                  <Col lg={3} sm={6}>
                    <div>
                      <p className="mb-2 text-uppercase fw-medium">Status :</p>
                      <h5 className="fs-14 mb-0">
                        <StatusBadge
                          value={visit.status}
                          map={amcVisitStatusMap}
                        />
                      </h5>
                    </div>
                  </Col>

                  {visit?.deleted_at != null && visit.deleted_at !== "" ? (
                    <Col lg={3} sm={6}>
                      <div>
                        <p className="mb-2 text-uppercase fw-medium text-danger">
                          Deleted
                        </p>
                        <h5 className="fs-14 mb-0 text-danger">
                          <DateTime date={visit.deleted_at} />
                        </h5>
                      </div>
                    </Col>
                  ) : null}
                </Row>
              </div>
            </div>
          </CardBody>
        </Card>
      </Col>
      <Col lg={4}>
        <Card className="mb-3">
          <CardBody>
            <h6 className="mb-4 text-uppercase text-muted">Timeline</h6>
            <Timeline steps={buildTimeline(visit)} />
          </CardBody>
        </Card>
        <AssignmentsOverviewCard
          sourceType={WorkAssignmentSourceType.AMC_VISIT}
          sourceId={visit.id}
          sourceLabel={visit.code ? `AMC visit ${visit.code}` : `AMC visit #${visit.id}`}
          canAssign={canCreateAssignment && shouldShowAmcVisitAssignmentActions(visit.status)}
          canReassign={canUpdateAssignment && canReassignAmcVisit(visit.status)}
          defaultPlannedStartAt={visit.scheduledAt}
          onSuccess={() => void refetch()}
        />
      </Col>
    </Row>
  );
};

export default OverviewTab;
